import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '@/shared/stores/authStore'
import type { OrcaTaskCompletedEvent, PlanCompletedEvent } from '@/shared/data/system-integrations.types'

interface UseAgentEventsOptions {
  onOrcaTaskCompleted?: (event: OrcaTaskCompletedEvent) => void
  onPlanCompleted?: (event: PlanCompletedEvent) => void
}

const SSE_URL = '/api/agents/events'
const MAX_RETRY_DELAY_MS = 30_000   // cap tại 30s
const BASE_RETRY_DELAY_MS = 2_000   // bắt đầu từ 2s

/**
 * Lắng nghe SSE events từ backend khi agents hoàn thành tasks/plans
 * Endpoint: GET /api/agents/events (SSE stream)
 *
 * Fix BUG-SSE-401:
 *  - Chỉ mở kết nối sau khi user authenticated (isLoggedIn)
 *  - EventSource browser không cho set Authorization header → dùng fetch()
 *    để detect 401 trước khi stream thay vì EventSource im lặng retry vô tận
 *  - Exponential backoff retry khi mất kết nối (network drop, server restart)
 *  - Khi nhận 401: không retry ngay — đợi user login lại (effect re-run)
 */
export function useAgentEvents({ onOrcaTaskCompleted, onPlanCompleted }: UseAgentEventsOptions) {
  const isLoggedIn = useAuthStore(s => !!s.user && !s.isLoading)
  const retryCount = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Stable refs để callback không gây re-render / re-connect
  const onOrcaRef = useRef(onOrcaTaskCompleted)
  const onPlanRef = useRef(onPlanCompleted)
  useEffect(() => { onOrcaRef.current = onOrcaTaskCompleted }, [onOrcaTaskCompleted])
  useEffect(() => { onPlanRef.current = onPlanCompleted }, [onPlanCompleted])

  const connect = useCallback(() => {
    // Hủy connection cũ nếu có
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    fetch(SSE_URL, {
      signal: ctrl.signal,
      headers: { Accept: 'text/event-stream' },
      credentials: 'include',   // gửi cookie session
    })
      .then(async res => {
        if (res.status === 401) {
          // Session hết hạn hoặc user chưa auth — không retry,
          // đợi isLoggedIn thay đổi để effect re-run
          console.warn('[AgentEvents] 401 — waiting for re-login')
          return
        }
        if (!res.ok) {
          throw new Error(`SSE ${res.status}`)
        }

        // Reset retry counter khi kết nối thành công
        retryCount.current = 0

        const reader = res.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done || ctrl.signal.aborted) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          let eventName = ''
          let dataLines: string[] = []

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim())
            } else if (line === '') {
              // Blank line = dispatch event
              if (eventName && dataLines.length > 0) {
                const raw = dataLines.join('\n')
                try {
                  const parsed = JSON.parse(raw)
                  if (eventName === 'orca.task_completed') {
                    onOrcaRef.current?.(parsed as OrcaTaskCompletedEvent)
                  } else if (eventName === 'plan.completed') {
                    onPlanRef.current?.(parsed as PlanCompletedEvent)
                  }
                } catch {
                  // ignore malformed JSON
                }
              }
              eventName = ''
              dataLines = []
            }
          }
        }
      })
      .catch(err => {
        if (ctrl.signal.aborted) return   // component unmounted, bỏ qua
        console.warn('[AgentEvents] SSE error, sẽ reconnect:', err)
      })
      .finally(() => {
        if (ctrl.signal.aborted) return   // unmounted — không retry

        // Exponential backoff: 2s, 4s, 8s, … tối đa 30s
        const delay = Math.min(
          BASE_RETRY_DELAY_MS * 2 ** retryCount.current,
          MAX_RETRY_DELAY_MS
        )
        retryCount.current += 1
        console.info(`[AgentEvents] reconnect trong ${delay}ms (lần ${retryCount.current})`)
        retryTimerRef.current = setTimeout(connect, delay)
      })
  }, []) // không dependency — stable callback

  useEffect(() => {
    if (!isLoggedIn) {
      // User chưa login hoặc đang logout — đóng kết nối hiện tại
      abortRef.current?.abort()
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
      retryCount.current = 0
      return
    }

    // User vừa login → reset retry và kết nối
    retryCount.current = 0
    connect()

    return () => {
      abortRef.current?.abort()
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }
  }, [isLoggedIn, connect])
}
